import * as pc from 'playcanvas';

import { applyInitialCamera, fitClipPlanes } from './apply-camera';
import { loadHdri } from './load-hdri';
import { loadModel } from './load-model';
import { loadSplat } from './load-splat';
import {
    SUPPORTED_SCHEMA_VERSION,
    type SceneFile
} from './scene-types';

/**
 * Runtime handle to a loaded scene. Returned by `loadScene` so callers can
 * inspect what was created, drive effect keyframes against it, or dispose it
 * when navigating to a new scene.
 */
export interface LoadedScene {
    /** Parsed scene.json. */
    file: SceneFile;
    /** Absolute base URL of the scene folder (always ends with '/'). */
    baseUrl: string;
    /** Root entity grouping every element loaded from this scene. */
    root: pc.Entity;
    /** Created splat entities, keyed by element id. */
    splats: Map<string, pc.Entity>;
    /** Created mesh entities, keyed by element id. */
    models: Map<string, pc.Entity>;
    /** HDRI source texture, if one was loaded. */
    hdriSource: pc.Texture | null;
    /** Bounding-sphere radius across all loaded splats (used to size LOD + camera). */
    sceneRadius: number;
    /** Bounding-sphere center across all loaded splats. */
    sceneCenter: pc.Vec3;
    /** Free all GPU resources owned by this scene. */
    dispose(): void;
}

export interface SceneLoadContext {
    app: pc.AppBase;
    cameraEntity: pc.Entity;
    /**
     * Resolve a scene-relative URL (as written in scene.json) to an absolute
     * URL the browser can fetch. Default is `new URL(rel, baseUrl).toString()`.
     */
    resolveUrl?: (rel: string, baseUrl: string) => string;
}

/**
 * Fetch a scene's JSON description and instantiate every element it declares
 * into the PlayCanvas scene graph. Returns once every element's top-level asset
 * has loaded so the caller can render with confidence (no half-loaded splats).
 *
 * For LOD-octree splats this does NOT mean the whole scene downloads first:
 * loadSplat awaits only the asset 'load' event, which for an octree fires when
 * lod-meta.json is parsed (~hundreds of KB) — the per-chunk .webp tiles then
 * stream lazily, per frame, as PlayCanvas resolves camera distance to octree
 * nodes. What's NOT yet done is an explicit coarse-first paint gate (render
 * LOD3 instantly, then refine); we render whatever PlayCanvas has resolved.
 * Full pipeline + the streaming details: documentation/LOD.md.
 */
export async function loadScene(
    sceneJsonUrl: string,
    ctx: SceneLoadContext
): Promise<LoadedScene> {
    // ── 1. Fetch + parse scene.json ───────────────────────────────────────
    const baseUrl = new URL('.', new URL(sceneJsonUrl, window.location.href)).toString();
    const resolve = ctx.resolveUrl ?? ((rel, base) => new URL(rel, base).toString());

    const res = await fetch(sceneJsonUrl);
    if (!res.ok) {
        throw new Error(`scene.json fetch failed: ${res.status} ${res.statusText} (${sceneJsonUrl})`);
    }
    const file = (await res.json()) as SceneFile;

    if (typeof file.schemaVersion !== 'number') {
        throw new Error(`scene.json missing schemaVersion (${sceneJsonUrl})`);
    }
    if (file.schemaVersion > SUPPORTED_SCHEMA_VERSION) {
        throw new Error(
            `scene.json schemaVersion ${file.schemaVersion} > supported ${SUPPORTED_SCHEMA_VERSION}; ` +
            'update viewer2.'
        );
    }

    // ── 2. Build a root entity for everything this scene owns ─────────────
    const root = new pc.Entity(`scene:${file.name ?? 'unnamed'}`);
    ctx.app.root.addChild(root);

    const splats = new Map<string, pc.Entity>();
    const models = new Map<string, pc.Entity>();
    let hdriSource: pc.Texture | null = null;

    // ── 2.5 Apply camera initial pose EARLY (before splat entities are added) ─
    // PlayCanvas's unified GSplat LOD evaluator runs on the splat's first
    // update tick after it's added to the scene. If the camera is at default
    // (origin-ish) but the scene's chunks are 100s of metres away, every
    // chunk is marked "too far" and gets evicted before it ever activates.
    // Subsequent camera moves do trigger re-eval, but only on lodUpdateDistance
    // threshold crossings — flaky. Applying the pose up front avoids the trap.
    applyInitialCamera(ctx.cameraEntity, file.camera);

    // ── 3. Load every element in parallel ─────────────────────────────────
    const loadJobs: Promise<unknown>[] = [];

    for (const el of file.splats ?? []) {
        loadJobs.push(
            loadSplat(ctx.app, root, el, resolve(el.url, baseUrl))
                .then((entity) => { splats.set(el.id, entity); })
        );
    }
    for (const el of file.models ?? []) {
        loadJobs.push(
            loadModel(ctx.app, root, el, resolve(el.url, baseUrl))
                .then((entity) => { models.set(el.id, entity); })
        );
    }
    if (file.hdri) {
        loadJobs.push(
            loadHdri(ctx.app, file.hdri, resolve(file.hdri.url, baseUrl))
                .then((tex) => { hdriSource = tex; })
        );
    }
    await Promise.all(loadJobs);

    // ── 4. LOD streaming wiring (canonical from PC lod-streaming example) ──
    // For LOD-octree splats, lodBaseDistance must be proportional to scene
    // scale; with the default of 5 nothing activates in scenes >50m across.
    // We size it to the largest splat's bounding-sphere radius.
    let sceneRadius = 1;
    let sceneCenter = new pc.Vec3();
    for (const entity of splats.values()) {
        const aabb = entity.gsplat?.customAabb;
        if (aabb) {
            const r = aabb.halfExtents.length();
            if (r > sceneRadius) sceneRadius = r;
            sceneCenter.copy(aabb.center);
        }
        const lodLevels = (entity.gsplat?.resource as { octree?: { lodLevels?: number } } | undefined)?.octree?.lodLevels;
        if (lodLevels && lodLevels > 0) {
            // Per-entity LOD: with only `lodLevels` available, any chunk whose
            // required LOD > lodLevels-1 gets evicted. Effective LOD scales as
            // log_multiplier(distance / lodBaseDistance), so to keep all chunks
            // alive at any camera distance up to ~5*sceneRadius we need:
            //
            //   lodBaseDistance >= maxCameraDistance / multiplier^(lodLevels-1)
            //
            // For lodLevels=1, multiplier^0 = 1, so lodBaseDistance must be
            // >= maxCameraDistance. We pick 4*sceneRadius as a comfortable
            // upper bound — typical orbit zoom-out is ~2-3x scene radius.
            const gs = entity.gsplat as unknown as {
                lodBaseDistance: number;
                lodMultiplier: number;
            };
            const span = Math.max(20, sceneRadius);
            gs.lodBaseDistance = lodLevels === 1 ? span * 4 : span;
            gs.lodMultiplier = 2;
        }
    }

    // Per-splat LOD ceiling (engine 2.20 moved lodRangeMin/Max from
    // scene.gsplat, where the setters are now no-ops, onto the component).
    // The floor is NOT touched here: it belongs to the boot clamp
    // (scene/lod-clamp.ts) and load-splat already applied it. NOTE:
    // `octree.lodLevels` (e.g. 1 for our bundle) is "detail tiers", not
    // per-node depth. The octree has many internal LOD levels mapped to tree
    // depth — setting lodRangeMax = lodLevels - 1 = 0 forbids all descended
    // chunks and nothing renders. Use a generous cap; PC clamps internally to
    // what's available.
    for (const entity of splats.values()) {
        if (entity.gsplat) entity.gsplat.lodRangeMax = 20;
    }

    // Re-assert the per-entity LOD knobs on the next animation frame. PC's
    // GSplat LOD evaluator runs its first pass when the component is first
    // added; setting lodBaseDistance/lodMultiplier during loader code (before
    // any tick) seems to be ignored on initial eval. The same setters DO
    // trigger immediate re-evaluation when called after the first tick, so
    // we defer one frame to kick chunk activation.
    requestAnimationFrame(() => {
        for (const entity of splats.values()) {
            const lodLevels = (entity.gsplat?.resource as { octree?: { lodLevels?: number } } | undefined)?.octree?.lodLevels;
            if (lodLevels && lodLevels > 0 && entity.gsplat) {
                const gs = entity.gsplat as unknown as {
                    lodBaseDistance: number;
                    lodMultiplier: number;
                };
                const span = Math.max(20, sceneRadius);
                gs.lodBaseDistance = lodLevels === 1 ? span * 4 : span;
                gs.lodMultiplier = 2;
            }
        }
    });

    // ── 5. Re-apply camera pose after load + fit clip planes ──────────────
    // (initial pose was applied in step 2.5; this re-asserts it in case
    // anything in the load path nudged it, and finalises the clip range.)
    applyInitialCamera(ctx.cameraEntity, file.camera);
    fitClipPlanes(ctx.cameraEntity, ctx.app.scene);

    // ── 6. Build the dispose closure ──────────────────────────────────────
    const dispose = () => {
        root.destroy();   // removes from parent + destroys children's components
        if (hdriSource) {
            hdriSource.destroy();
            // envAtlas / skybox are owned by app.scene and replaced on next load
        }
    };

    return {
        file,
        baseUrl,
        root,
        splats,
        models,
        hdriSource,
        sceneRadius,
        sceneCenter,
        dispose
    };
}
